import { Injectable } from "@angular/core";
import { BehaviorSubject, interval, of, Subject } from "rxjs";
import { catchError, exhaustMap, takeUntil } from "rxjs/operators";
import {
  EXTERNAL_ENDPOINT_TEST,
  LATENCY_CS_ENDPOINT_GOOD,
  LATENCY_CS_ENDPOINT_MID,
  NetworkStatus,
} from "src/app/config/network-check";

@Injectable({
  providedIn: "root",
})
export class ConnectionStatusService {
  private readonly THRESHOLD_MS_GOOD = LATENCY_CS_ENDPOINT_GOOD;
  private readonly THRESHOLD_MS_MEDIUM = LATENCY_CS_ENDPOINT_MID;
  private latencies: number[] = [];
  private goodStreak = 0;
  private backgroundMonitorStarted = false;

  private slowTrigger$ = new Subject<void>();
  public stopBackgroundMonitoring$ = new Subject<void>();
  public readonly status$ = new BehaviorSubject<NetworkStatus>("unknown");
  private sampleCount = 0;

  constructor() {
    this.slowTrigger$
      .pipe(exhaustMap(() => this.runExternalTest()))
      .subscribe((externalClass) => {
        this.setStatus(externalClass);
      });
  }

  reportSuccess(latency: number) {
    this.pushLatency(latency);
    this.sampleCount++;

    const avg = this.averageLatency();
    const avgClass = this.classifyLatency(avg);

    if (this.sampleCount < 3) return;

    if (avgClass === "good") {
      this.goodStreak++;
      if (this.goodStreak >= 3) {
        this.setStatus("good");
      }
      return;
    }

    if (avgClass === "slow" || avgClass === "offline") {
      this.goodStreak = 0;
      this.slowTrigger$.next();
    }
  }

  startBackgroundInternetMonitor() {
    if (this.backgroundMonitorStarted) return;
    this.backgroundMonitorStarted = true;

    interval(60_000) // 1 min
      .pipe(
        takeUntil(this.stopBackgroundMonitoring$),
        exhaustMap(() => this.runExternalTest()),
        catchError(() => of("offline" as const)),
      )
      .subscribe((status) => {
        this.setStatus(status);
      });
  }

  stopBackgroundInternetMonitor() {
    this.stopBackgroundMonitoring$.next();
    this.backgroundMonitorStarted = false;
  }

  private pushLatency(latency: number) {
    this.latencies.push(latency);
    if (this.latencies.length > this.THRESHOLD_MS_GOOD) {
      this.latencies.shift();
    }
  }

  private averageLatency(): number {
    if (!this.latencies.length) return 0;
    return this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length;
  }

  private classifyLatency(lat: number): NetworkStatus {
    if (lat < this.THRESHOLD_MS_GOOD) return "good";
    if (lat < this.THRESHOLD_MS_MEDIUM) return "slow";
    return "offline";
  }

  private setStatus(next: NetworkStatus) {
    const current = this.status$.value;
    if (current === next) return;

    this.status$.next(next);
  }

  private runExternalTest(): Promise<NetworkStatus> {
    return new Promise((resolve) => {
      const TEST_URL = EXTERNAL_ENDPOINT_TEST;

      const start = performance.now();

      fetch(TEST_URL, { mode: "no-cors", cache: "no-store" })
        .then(() => {
          const latency = performance.now() - start;
          resolve(this.classifyLatency(latency));
        })
        .catch(() => {
          resolve("offline");
        });
    });
  }
}
